// frontend/src/hooks/useCategories.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { categoryService, CategoryFilters, CategoryFormData } from '@/lib/api/categoryService';
import { tokenManager } from '@/lib/httpClient';

// Query keys for categories
export const categoryKeys = {
  all: ['categories'] as const,
  lists: () => [...categoryKeys.all, 'list'] as const,
  list: (filters: CategoryFilters) => [...categoryKeys.lists(), filters] as const,
  details: () => [...categoryKeys.all, 'detail'] as const,
  detail: (id: number) => [...categoryKeys.details(), id] as const,
  tree: () => [...categoryKeys.all, 'tree'] as const,
  parents: () => [...categoryKeys.all, 'parents'] as const,
  subcategories: (parentId: number) => [...categoryKeys.all, 'subcategories', parentId] as const,
  dropdown: () => [...categoryKeys.all, 'dropdown'] as const,
  stats: () => [...categoryKeys.all, 'stats'] as const,
} as const;

// Get categories with filters
export const useCategories = (filters: CategoryFilters = {}) => {
  return useQuery({
    queryKey: categoryKeys.list(filters),
    queryFn: async () => {
      const response = await categoryService.getAll(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get single category by ID
export const useCategory = (id: number) => {
  return useQuery({
    queryKey: categoryKeys.detail(id),
    queryFn: async () => {
      const response = await categoryService.getById(id);
      if (response.success && response.data) {
        return response.data.category;
      }
      throw new Error(response.message);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Create new category
export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CategoryFormData) => {
      const response = await categoryService.create(data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (newCategory) => {
      // Invalidate category lists
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.tree() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.parents() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.dropdown() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.stats() });
      
      // Add new category to cache if we have the data
      if (newCategory?.category) {
        queryClient.setQueryData(
          categoryKeys.detail(newCategory.category.category_id),
          newCategory.category
        );
      }
    },
  });
};

// Update category
export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CategoryFormData }) => {
      const response = await categoryService.update(id, data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (updatedCategory, variables) => {
      // Update specific category in cache
      if (updatedCategory?.category) {
        queryClient.setQueryData(
          categoryKeys.detail(variables.id),
          updatedCategory.category
        );
      }
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.tree() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.parents() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.dropdown() });
    },
  });
};

// Delete category
export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await categoryService.delete(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: categoryKeys.detail(variables) });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.tree() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.parents() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.dropdown() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.stats() });
    },
  });
};

// Toggle category status
export const useToggleCategoryStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await categoryService.toggleStatus(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (updatedCategory, variables) => {
      // Update specific category in cache
      if (updatedCategory?.category) {
        queryClient.setQueryData(
          categoryKeys.detail(variables),
          updatedCategory.category
        );
      }
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: categoryKeys.dropdown() });
    },
  });
};

// Get parent categories
export const useParentCategories = () => {
  return useQuery({
    queryKey: categoryKeys.parents(),
    queryFn: async () => {
      const response = await categoryService.getParentCategories();
      if (response.success && response.data) {
        return response.data.categories;
      }
      throw new Error(response.message);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get subcategories
export const useSubcategories = (parentId: number) => {
  return useQuery({
    queryKey: categoryKeys.subcategories(parentId),
    queryFn: async () => {
      const response = await categoryService.getSubcategories(parentId);
      if (response.success && response.data) {
        return response.data.categories;
      }
      throw new Error(response.message);
    },
    enabled: !!parentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get category tree
export const useCategoryTree = () => {
  return useQuery({
    queryKey: categoryKeys.tree(),
    queryFn: async () => {
      const response = await categoryService.getCategoryTree();
      if (response.success && response.data) {
        return response.data.categories;
      }
      throw new Error(response.message);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get dropdown options
export const useCategoryDropdownOptions = () => {
  return useQuery({
    queryKey: categoryKeys.dropdown(),
    queryFn: async () => {
      const response = await categoryService.getCategoryDropdownOptions();
      if (response.success && response.data) {
        return response.data.categories;
      }
      throw new Error(response.message);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Reorder categories
export const useReorderCategories = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryOrders: { category_id: number; display_order: number }[]) => {
      const response = await categoryService.reorderCategories(categoryOrders);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate all category queries to reflect new order
      queryClient.invalidateQueries({ queryKey: categoryKeys.all });
    },
  });
};


// Search categories
export const useSearchCategories = (searchQuery: string, filters: CategoryFilters = {}) => {
  return useQuery({
    queryKey: [...categoryKeys.lists(), 'search', searchQuery, filters],
    queryFn: async () => {
      const response = await categoryService.searchCategories(searchQuery, filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!searchQuery && searchQuery.length > 2,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get category statistics
export const useCategoryStats = () => {
  return useQuery({
    queryKey: categoryKeys.stats(),
    queryFn: async () => {
      const response = await categoryService.getCategoryStats();
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Upload category icon
export const useUploadCategoryIcon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, file }: { id: number; file: File }) => {
      const response = await categoryService.uploadCategoryIcon(id, file);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate the specific category to refetch with new icon
      queryClient.invalidateQueries({ 
        queryKey: categoryKeys.detail(variables.id) 
      });
      
      // Invalidate lists to show updated icon
      queryClient.invalidateQueries({ queryKey: categoryKeys.lists() });
    },
  });
};

// Check if category can be deleted
export const useCanDeleteCategory = (id: number) => {
  return useQuery({
    queryKey: [...categoryKeys.detail(id), 'can-delete'],
    queryFn: async () => {
      const response = await categoryService.canDeleteCategory(id);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!id,
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

